import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// A desktop home (assistants/_home_desktop): widgets on a grid of cells across
// the whole window, like widgets on a Mac desktop.
//
// Each widget carries data-desktop-key (its pin id), data-size (small, medium
// or large: one cell, two across, two by two — see SPANS), data-sizes (the
// sizes it offers) and, once placed, data-col / data-row. How many columns there are is the
// window's business: as many cells of at least MIN_CELL as fit. Placed widgets
// go to their cell, clamped to the grid; anything unplaced, or bumped because
// its cell is taken, fills the first free cells reading left to right.
//
// Moving and resizing happen in edit mode only (Edit widgets → Done), so a
// click on a headline is never a drag. While a widget is dragged, the others
// make room live and an outlined slot shows where it will land; a drop saves
// every widget's cell, since the grid is now exactly what you see.
//
// Below 768px the controller stands down and the CSS stacks the widgets.
const MIN_CELL = 200
// A grid held to two or four widget columns (Dashboard::GRID_CELLS) stops its
// cells growing past this, and centres itself in whatever width is left.
const MAX_CELL = 240
const GAP = 16
// Rows are a third of a widget's height, so a box can be shorter than a widget:
// three of them (and the two gaps between) make the 168px of a small widget,
// six make the 352px of a large one, and Jane's ask card takes two, about
// 107px — the shape it had above the column.
const WIDGET_HEIGHT = 168
const ROW_HEIGHT = (WIDGET_HEIGHT - 2 * GAP) / 3
const DRAG_THRESHOLD = 4
// [cells across, rows down]. "wide" is Jane's ask card only: as wide as two
// medium widgets, two rows tall. "short" is a dial's S: one cell, as tall as
// the ask card, so dials can line up beside it.
// "wide_tall" is the ask card with its row of quick actions under it.
const SPANS = { short: [1, 2], small: [1, 3], medium: [2, 3], large: [2, 6], wide: [4, 2], wide_tall: [4, 3] }

export default class extends Controller {
    static targets = ["canvas", "item", "slot", "menu"]
    // cells: the most cells across (4 or 8), or 0 for the window's width.
    static values = { url: String, settingsUrl: String, editable: Boolean, cells: Number }

    connect() {
        this.media = window.matchMedia("(min-width: 768px)")
        this._onMedia = () => this.layout()
        this.media.addEventListener("change", this._onMedia)

        this._observer = new ResizeObserver(() => this._scheduleLayout())
        this._observer.observe(this.canvasTarget)

        this._onPointerDown = (event) => this._pointerDown(event)
        this._onDragStart = (event) => { if (this._editing) event.preventDefault() }
        this._onClick = (event) => this._swallowClick(event)
        this.canvasTarget.addEventListener("pointerdown", this._onPointerDown)
        this.canvasTarget.addEventListener("dragstart", this._onDragStart)
        // Capture: in edit mode a widget's links do nothing, so a click meant to
        // pick one up never opens the page behind it.
        this.canvasTarget.addEventListener("click", this._onClick, true)

        this.layout()
    }

    disconnect() {
        this.closeMenu()
        this.media.removeEventListener("change", this._onMedia)
        this._observer?.disconnect()
        this.canvasTarget.removeEventListener("pointerdown", this._onPointerDown)
        this.canvasTarget.removeEventListener("dragstart", this._onDragStart)
        this.canvasTarget.removeEventListener("click", this._onClick, true)
        this._endDrag()
        cancelAnimationFrame(this._frame)
    }

    itemTargetConnected() {
        this._scheduleLayout()
    }

    itemTargetDisconnected() {
        this._scheduleLayout()
    }

    // ---- The widget menu ("…" on hover, or a right-click) ----

    // Fills the one menu on the canvas from this widget's template and opens it
    // under the "…" (or at the pointer, for a right-click).
    openMenu(event) {
        if (this._editing || !this.hasMenuTarget || !this._active()) return
        // A right-click on a link keeps the browser's menu (open in a new tab).
        if (event.type === "contextmenu" && event.target.closest("a")) return
        const item = event.target.closest("[data-home-desktop-target~='item']")
        const template = item?.querySelector("template.home-desktop-menu-template")
        if (!template) return
        event.preventDefault()
        event.stopPropagation()
        if (this._menuItem === item && !this.menuTarget.hidden && event.type === "click") return this.closeMenu()

        this.closeMenu()
        this._menuItem = item
        item.classList.add("has-menu")
        const menu = this.menuTarget
        menu.replaceChildren(template.content.cloneNode(true))
        menu.querySelectorAll("[data-size]").forEach((option) => {
            option.setAttribute("aria-checked", String(option.dataset.size === item.dataset.size))
        })
        menu.hidden = false

        const canvas = this.canvasTarget.getBoundingClientRect()
        const anchor = event.type === "contextmenu"
            ? { left: event.clientX, top: event.clientY, right: event.clientX }
            : event.currentTarget.getBoundingClientRect()
        const width = menu.offsetWidth
        const height = menu.offsetHeight
        let left = (event.type === "contextmenu" ? anchor.left : anchor.right - width) - canvas.left
        left = Math.max(0, Math.min(left, canvas.width - width))
        // Below the "…" (or the pointer); above it when that would run off the
        // bottom of the window, as on a card in the last row. Never off the top.
        const below = event.type === "contextmenu" ? anchor.top : anchor.bottom + 6
        const above = (event.type === "contextmenu" ? anchor.top : anchor.top - 6) - height
        const viewportTop = below + height > window.innerHeight - 8 && above >= 8 ? above : Math.max(8, Math.min(below, window.innerHeight - height - 8))
        Object.assign(menu.style, { left: `${left}px`, top: `${viewportTop - canvas.top}px` })
        menu.querySelector("a, button")?.focus({ preventScroll: true })

        this._onOutside = (e) => { if (!menu.contains(e.target)) this.closeMenu() }
        this._onScrollAway = () => this.closeMenu()
        setTimeout(() => {
            document.addEventListener("pointerdown", this._onOutside, true)
            window.addEventListener("scroll", this._onScrollAway, { capture: true, once: true })
        }, 0)
    }

    closeMenu() {
        if (!this.hasMenuTarget || this.menuTarget.hidden) return
        this.menuTarget.hidden = true
        this.menuTarget.replaceChildren()
        this._menuItem?.classList.remove("has-menu")
        this._menuItem = null
        document.removeEventListener("pointerdown", this._onOutside, true)
        window.removeEventListener("scroll", this._onScrollAway, { capture: true })
    }

    // "Remove widget" (or "Remove stack": every dial in it): unpins each
    // (DELETE /pins/:id), and the rest close up.
    async removeWidget(event) {
        const item = this._menuItem
        const urls = [].concat(event.params.urls || [])
        this.closeMenu()
        if (!item || urls.length === 0) return
        try {
            const responses = await Promise.all(urls.map((url) => fetch(url, {
                method: "DELETE",
                headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
                credentials: "same-origin"
            })))
            if (responses.every((response) => response.ok)) item.remove()
        } catch {
            // Left in place: nothing was removed.
        }
    }

    // A widget's own setting from its menu (which task lists, which feed
    // channel): saved on the widget's pin (PATCH /home/widgets/settings), then the
    // card's frame is asked again. A checkbox group stays open to tick more; a
    // radio choice closes the menu.
    async saveSetting(event) {
        const item = this._menuItem
        const button = event.currentTarget
        const { pin, field, value, multiple } = event.params
        if (!item || !pin || !field) return

        const body = { pin_id: pin }
        if (multiple) {
            button.setAttribute("aria-checked", String(button.getAttribute("aria-checked") !== "true"))
            body[field] = [...this.menuTarget.querySelectorAll("[data-home-desktop-field-param]")]
                .filter((option) => option.dataset.homeDesktopFieldParam === field && option.getAttribute("aria-checked") === "true")
                .map((option) => option.dataset.homeDesktopValueParam)
        } else {
            body[field] = String(value)
            this.closeMenu()
        }
        try {
            const response = await fetch(this.settingsUrlValue, {
                method: "PATCH",
                headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
                credentials: "same-origin",
                body: JSON.stringify(body)
            })
            if (response.ok) item.querySelectorAll("turbo-frame[src]").forEach((frame) => frame.reload())
        } catch {
            // Not saved: the card keeps showing what it did.
        }
    }

    // ---- Stacks (dials sharing one card, taking turns) ----

    // "Stack with …": this card, and whatever was stacked on it, joins the
    // chosen card's stack. The page reloads to draw the stack.
    async stackWith(event) {
        const item = this._menuItem
        const lead = String(event.params.lead)
        this.closeMenu()
        if (!item || !lead) return
        const positions = { [item.dataset.desktopKey]: { stack: lead } }
        this._stackMembers(item).forEach((key) => { positions[key] = { stack: lead } })
        await this._send({ positions })
        this._reload()
    }

    // "Unstack": every member goes back into the grid on its own, in the first
    // free cells.
    async unstack(event) {
        const members = [].concat(event.params.members || [])
        this.closeMenu()
        if (members.length === 0) return
        const positions = {}
        members.forEach((key) => { positions[key] = null })
        await this._send({ positions })
        this._reload()
    }

    // "Turn every …": how long each dial in the stack stays up.
    stackEvery(event) {
        const item = this._menuItem
        const every = Number(event.params.every)
        this.closeMenu()
        if (!item || !every) return
        item.setAttribute("data-widget-stack-every-value", String(every))
        this._send({ positions: { [item.dataset.desktopKey]: this._cellOf(item, { every }) } })
    }

    _stackMembers(item) {
        const unstack = item.querySelector("template.home-desktop-menu-template")?.content.querySelector("[data-home-desktop-members-param]")
        try { return JSON.parse(unstack?.dataset.homeDesktopMembersParam || "[]") } catch { return [] }
    }

    _cellOf(item, extra = {}) {
        const cell = { col: Number(item.dataset.resolvedCol ?? item.dataset.col), row: Number(item.dataset.resolvedRow ?? item.dataset.row), size: item.dataset.size }
        const every = Number(item.getAttribute("data-widget-stack-every-value"))
        if (every) cell.every = every
        return { ...cell, ...extra }
    }

    _reload() {
        if (window.Turbo) window.Turbo.visit(window.location.href, { action: "replace" })
        else window.location.reload()
    }

    // ---- Edit mode ----

    edit() {
        this.closeMenu()
        if (!this.editableValue) return
        this._editing = true
        this.element.classList.add("is-editing")
        this.layout()
    }

    done() {
        this._editing = false
        this.element.classList.remove("is-editing")
        this.layout()
    }

    // "Tidy up": forget every cell and fill the grid in order, sizes kept.
    cleanUp() {
        this._priority = null
        this.itemTargets.forEach((item) => { delete item.dataset.col; delete item.dataset.row })
        this.layout()
        this._saveAll()
    }

    resize(event) {
        const item = event.target.closest("[data-home-desktop-target~='item']") || this._menuItem
        const size = event.params.size
        this.closeMenu()
        if (!item || !SPANS[size] || item.dataset.size === size) return

        item.dataset.size = size
        item.querySelectorAll(".home-desktop-size").forEach((button) => {
            button.setAttribute("aria-pressed", String(button.dataset.homeDesktopSizeParam === size))
        })
        // The frame (every frame, in a stack) asks again for what fits the new box.
        item.querySelectorAll("turbo-frame[src]").forEach((frame) => {
            const url = new URL(frame.src, window.location.href)
            url.searchParams.set("size", size)
            frame.src = url.toString()
        })
        // The resized widget keeps its cell; whatever it now overlaps moves.
        this._priority = item
        this.layout()
        this._saveAll()
    }

    _swallowClick(event) {
        if (!this._editing && !this._justDragged) return
        if (event.target.closest(".home-desktop-sizes")) return
        if (!event.target.closest("[data-home-desktop-target~='item']")) return
        event.preventDefault()
        event.stopPropagation()
    }

    // ---- Layout ----

    _active() {
        return this.media.matches
    }

    _scheduleLayout() {
        if (this._drag?.moving) return
        cancelAnimationFrame(this._frame)
        this._frame = requestAnimationFrame(() => this.layout())
    }

    // How many cells across, how wide each is, and how far in the grid starts
    // (a capped grid sits centred in a wider window).
    _metrics() {
        const width = this.canvasTarget.clientWidth
        let cols = Math.max(2, Math.floor((width + GAP) / (MIN_CELL + GAP)))
        if (this.cellsValue > 0) cols = Math.min(cols, this.cellsValue)
        let cell = (width - (cols - 1) * GAP) / cols
        if (this.cellsValue > 0) cell = Math.min(cell, MAX_CELL)
        const offset = Math.max(0, (width - (cols * cell + (cols - 1) * GAP)) / 2)
        return { cols, cell, offset }
    }

    _span(item, cols) {
        const [w, h] = SPANS[item.dataset.size] || SPANS.medium
        return [Math.min(w, cols), h]
    }

    // Where every widget goes. `override` is the widget being dragged and the
    // cell under it: it is placed first so the rest make room around it.
    _resolve(override = null) {
        const { cols } = this._metrics()
        const taken = new Set()
        const fits = (col, row, w, h) => {
            if (col < 0 || col + w > cols || row < 0) return false
            for (let r = row; r < row + h; r++) {
                for (let c = col; c < col + w; c++) if (taken.has(`${c},${r}`)) return false
            }
            return true
        }
        const take = (col, row, w, h) => {
            for (let r = row; r < row + h; r++) for (let c = col; c < col + w; c++) taken.add(`${c},${r}`)
        }
        // First free cell at or after (col, row), reading left to right.
        const firstFit = (w, h, startRow = 0, startCol = 0) => {
            for (let row = startRow; ; row++) {
                for (let col = row === startRow ? startCol : 0; col + w <= cols; col++) {
                    if (fits(col, row, w, h)) return { col, row }
                }
            }
        }

        const placed = new Map()
        const place = (item, col, row) => {
            const [w, h] = this._span(item, cols)
            take(col, row, w, h)
            placed.set(item, { col, row, w, h })
        }

        const first = override?.item || this._priority
        if (override) {
            const [w] = this._span(override.item, cols)
            place(override.item, Math.max(0, Math.min(override.col, cols - w)), Math.max(0, override.row))
        }

        const saved = []
        const unplaced = []
        this.itemTargets.forEach((item) => {
            if (placed.has(item)) return
            const col = parseInt(item.dataset.col, 10)
            const row = parseInt(item.dataset.row, 10)
            if (Number.isFinite(col) && Number.isFinite(row)) saved.push({ item, col, row })
            else unplaced.push(item)
        })
        saved.sort((a, b) => (b.item === first) - (a.item === first) || a.row - b.row || a.col - b.col)

        saved.forEach(({ item, col, row }) => {
            const [w, h] = this._span(item, cols)
            const wanted = Math.max(0, Math.min(col, cols - w))
            if (fits(wanted, row, w, h)) return place(item, wanted, row)
            const spot = firstFit(w, h, row, wanted)
            place(item, spot.col, spot.row)
        })
        unplaced.forEach((item) => {
            const [w, h] = this._span(item, cols)
            const spot = firstFit(w, h)
            place(item, spot.col, spot.row)
        })
        return placed
    }

    layout(override = null) {
        const canvas = this.canvasTarget
        if (!this._active()) {
            canvas.classList.remove("is-active")
            canvas.style.height = ""
            this.itemTargets.forEach((item) => {
                ["left", "top", "width", "height"].forEach((prop) => { item.style[prop] = "" })
            })
            return
        }

        canvas.classList.add("is-active")
        const { cell, offset } = this._metrics()
        const placed = this._resolve(override)
        const boxes = this._fitComposer(placed, cell, offset)
        let bottom = 0
        placed.forEach((spot, item) => {
            const box = boxes.get(item)
            bottom = Math.max(bottom, box.top + box.height)
            item.dataset.resolvedCol = spot.col
            item.dataset.resolvedRow = spot.row
            if (override && item === override.item) return this._showSlot(box)
            Object.assign(item.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` })
        })
        if (!override) this._hideSlot()
        // In edit mode there is always an empty row under the last widget to
        // drop into.
        const spare = this._editing ? WIDGET_HEIGHT + GAP : 0
        canvas.style.height = `${bottom + spare}px`
    }

    // Jane's ask card keeps its own height rather than stretching to the rows
    // it spans: stretched, the field stood a row taller than it needs (two
    // rows, three with quick actions). The card is measured where it sits, and
    // whatever is under it in its columns moves up by what it gave back -
    // never above anything else that ends higher in the same columns.
    _fitComposer(placed, cell, offset) {
        const boxes = new Map()
        placed.forEach((spot, item) => boxes.set(item, this._box(spot, cell, offset)))
        const composer = Array.from(placed.keys()).find((item) => item.dataset.desktopKey === "composer")
        if (!composer) return boxes

        const spot = placed.get(composer)
        const box = boxes.get(composer)
        Object.assign(composer.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: "" })
        const natural = composer.offsetHeight
        const give = box.height - natural
        if (!natural || give <= 0) return boxes
        box.height = natural

        const overlaps = (a, b) => a.col < b.col + b.w && b.col < a.col + a.w
        const below = Array.from(placed.entries())
            .filter(([item, other]) => item !== composer && other.row >= spot.row + spot.h && overlaps(other, spot))
            .sort((a, b) => a[1].row - b[1].row)
        below.forEach(([item, other]) => {
            const mine = boxes.get(item)
            let floor = 0
            placed.forEach((above, aboveItem) => {
                if (aboveItem === item || above.row + above.h > other.row || !overlaps(above, other)) return
                const aboveBox = boxes.get(aboveItem)
                floor = Math.max(floor, aboveBox.top + aboveBox.height + GAP)
            })
            mine.top = Math.max(floor, mine.top - give)
        })
        return boxes
    }

    _box({ col, row, w, h }, cell, offset = 0) {
        return {
            left: offset + col * (cell + GAP),
            top: row * (ROW_HEIGHT + GAP),
            width: w * cell + (w - 1) * GAP,
            height: h * ROW_HEIGHT + (h - 1) * GAP
        }
    }

    _showSlot(box) {
        if (!this.hasSlotTarget) return
        Object.assign(this.slotTarget.style, { left: `${box.left}px`, top: `${box.top}px`, width: `${box.width}px`, height: `${box.height}px` })
        this.slotTarget.classList.add("is-visible")
    }

    _hideSlot() {
        if (this.hasSlotTarget) this.slotTarget.classList.remove("is-visible")
    }

    // ---- Dragging (edit mode only) ----

    _pointerDown(event) {
        if (!this._editing || !this._active() || event.button !== 0) return
        if (event.target.closest(".home-desktop-sizes")) return
        const item = event.target.closest("[data-home-desktop-target~='item']")
        if (!item) return

        this._drag = {
            item,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            originLeft: item.offsetLeft,
            originTop: item.offsetTop,
            moving: false
        }
        this._onMove = (e) => this._pointerMove(e)
        this._onUp = (e) => this._pointerUp(e)
        document.addEventListener("pointermove", this._onMove)
        document.addEventListener("pointerup", this._onUp)
        document.addEventListener("pointercancel", this._onUp)
    }

    _pointerMove(event) {
        const drag = this._drag
        if (!drag || event.pointerId !== drag.pointerId) return
        const dx = event.clientX - drag.startX
        const dy = event.clientY - drag.startY
        if (!drag.moving) {
            if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return
            drag.moving = true
            drag.item.classList.add("is-dragging")
            this.canvasTarget.classList.add("is-dragging")
        }
        event.preventDefault()

        const { cols, cell, offset } = this._metrics()
        const [w] = this._span(drag.item, cols)
        const maxLeft = this.canvasTarget.clientWidth - offset - drag.item.offsetWidth
        const left = Math.max(offset, Math.min(drag.originLeft + dx, maxLeft))
        const top = Math.max(0, drag.originTop + dy)
        drag.item.style.left = `${left}px`
        drag.item.style.top = `${top}px`

        // The cell nearest the card's top-left corner.
        const col = Math.max(0, Math.min(Math.round((left - offset) / (cell + GAP)), cols - w))
        const row = Math.max(0, Math.round(top / (ROW_HEIGHT + GAP)))
        if (drag.col === col && drag.row === row) return
        drag.col = col
        drag.row = row
        this.layout({ item: drag.item, col, row })
    }

    _pointerUp(event) {
        const drag = this._drag
        if (!drag || event.pointerId !== drag.pointerId) return
        const moved = drag.moving
        this._endDrag()
        if (!moved) return

        // The click that ends a drag is not a click on the card.
        this._justDragged = true
        setTimeout(() => { this._justDragged = false }, 0)

        drag.item.dataset.col = drag.item.dataset.resolvedCol
        drag.item.dataset.row = drag.item.dataset.resolvedRow
        this._priority = drag.item
        this.layout()
        this._saveAll()
    }

    _endDrag() {
        const drag = this._drag
        if (!drag) return
        drag.item.classList.remove("is-dragging")
        this.canvasTarget.classList.remove("is-dragging")
        document.removeEventListener("pointermove", this._onMove)
        document.removeEventListener("pointerup", this._onUp)
        document.removeEventListener("pointercancel", this._onUp)
        this._drag = null
    }

    // Every widget's cell and size as laid out now. Cells are fixed sizes, so
    // what you see is the whole arrangement and saving all of it is safe.
    _saveAll() {
        if (!this._active()) return
        const positions = {}
        this.itemTargets.forEach((item) => {
            const key = item.dataset.desktopKey
            if (!key) return
            item.dataset.col = item.dataset.resolvedCol
            item.dataset.row = item.dataset.resolvedRow
            positions[key] = this._cellOf(item)
        })
        this._send({ positions })
    }

    async _send(body) {
        try {
            await fetch(this.urlValue, {
                method: "PATCH",
                headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
                credentials: "same-origin",
                body: JSON.stringify(body)
            })
        } catch {
            // A failed save leaves the widgets where you put them for this visit;
            // the next change sends the whole grid again.
        }
    }
}
